import LazyTable, {
  BasicCellType,
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ListRequestOfBaseFilter,
  SkuCertificate,
} from "services/KnowfullyAdminClient";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import contextStore from "stores/contextStore";
import { useState } from "react";

export interface SkuCertificateModel extends SkuCertificate {
  id: string;
  certificateAssociation: string;
}

type SkuCertificatesListProps = {
  skuId: string;
};

const SkuCertificatesList = ({ skuId }: SkuCertificatesListProps) => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const {
    certificateService,
    certificateDetailsUrl,
    skuDetailsUrl,
  } = useGlobalContext();

  const { organizationId } = contextStore.useState();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const { data, isFetching } = useQuery(
    [
      "skuCertificates",
      skuId,
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
    ],
    () =>
      certificateService.findCertificatesBySkuOrProduct(
        skuId,
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
        })
      )
  );

  const rows = (data?.data ?? []).map(
    (e) =>
      ({
        ...e,
        certificateAssociation: t({
          id: e.foundBySku
            ? "skuCertificateAssociationLabel"
            : "productCertificateAssociationLabel",
        }),
      } as SkuCertificateModel)
  );

  const baseCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...baseCell,
      accessor: "organizationName",
      label: t({ id: "organization" }),
    },
    {
      ...baseCell,
      accessor: "name",
      label: t({ id: "nameLabel" }),
    },
    {
      ...baseCell,
      accessor: "description",
      label: t({ id: "descriptionLabel" }),
    },
    {
      ...baseCell,
      accessor: "certificateAssociation",
      customSortAccessor: "foundBySku",
      label: t({ id: "certificateAssociationLabel" }),
    },
  ];

  const onClick = (id: string, row: any) => {
    navigate(certificateDetailsUrl(row.id), {
      state: {
        backButtonProps: {
          redirectRoute: skuDetailsUrl(skuId),
          labelKey: "backToSkuButtonLabel",
        },
      },
    });
  };

  return (
    <>
      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isFetching,
          noResultsLabel: t({ id: "noCertificatesLabel" }),
          allowPagination: rows.length !== 0 && !isFetching,
          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => certificateDetailsUrl(id)),
          ],
        }}
      />
    </>
  );
};

export default SkuCertificatesList;
